import { callGetExternalApi, callPostExternalApi } from '@ctn/axios-util';

import { useSessionStore } from '@/store/session.store';

import { getUrlTramite } from '@/helpers/navigation';
import type {
	IDocumento,
	ISolicitudDocumentacion,
	ISubsanacionDocumento,
	ISubsanacionSujeto,
	ISujetoListado,
	ITramite,
	ITramiteListado,
	ITramiteTMU,
} from '@/interfaces/model/don.interface';
import { type TipoYEstadoItem } from '@/interfaces/model/estadisticas.interface';
import type { IErrorResponse } from '@/interfaces/model/fetch.interface';
import { type IListado, type ILookup, type IPagination } from '@/interfaces/model/generic.interface';
import { processError } from '@/utils/axiosErrors';
import axiosInstance from '@/utils/axiosInterceptor';

const getBaseUrl = () => {
	const tramiteData = useSessionStore.getState().getTramiteData();

	if (!tramiteData) {
		throw new Error('No hay un trámite seleccionado para cargar los servicios');
	}

	return getUrlTramite(tramiteData);
};

export type IOperacionPpoParams = {
	codigoEF: string;
	numeroPrestamo: string;
	codigoProducto: string;
};

type IParams = {
	idSubsanacion?: number;
	nifEmisor?: string;
	idTipoSector?: number;
	idTramite?: number;
	idTipoSubsanacion?: number;
};

export type ITramitesListadoPayload = IPagination & {
	fechaDesde?: string;
	fechaHasta?: string;
	identificadorUnico?: string;
	numeroTramite?: string;
	listaIdsEstados?: number[];
	listaIdsTipos?: number[];
	listaIdsEstadosSubsanacionFirma?: number[];
	numeroDocumento?: string;
};

export type ISolicitudDocumentacionPayload = IPagination & {
	fechaDesde?: string;
	fechaHasta?: string;
	numeroTramite?: string;
	listaIdsEstados?: number[];
};

export type ITramitesListadoResponse = IListado<ITramiteListado>;
export type ISolicitudDocumentacionResponse = IListado<ISolicitudDocumentacion>;

export type ITramiteGetPayload = {
	nifEmisor: string;
	idTipoSector: number;
	idTramite: number;
};

export type DocumentoContenidoPayload = {
	idDocumento: number;
	idTramite: number;
	nifEmisor: string;
};

export type CancelacionPayload = {
	motivo: string;
	idTramite: number;
	nifEmisor: string;
	numeroTramite: string;
	tipoSector: ILookup;
};

export type ISubsancionSinErroresPayload = {
	nifEmisor: string;
	idError: number;
	idTramite: number;
	idTipoError: number;
	observacionesEmisor: string;
	tipoSector: ILookup;
};

export type IErroresDocumentoPayload = ISubsancionSinErroresPayload & { documento: IDocumento };
export type IErroresSujetoPayload = ISubsancionSinErroresPayload & { sujeto: ISujetoListado };

export type ISubsanacionListadoPayload = IPagination & {
	listaIdsEstados: number[];
	idTipoSubsanacion?: number;
};

export type ISubsanacionListadoResponse = IListado<ISubsanacionDocumento | ISubsanacionSujeto>;

export type EstadisticaResponse = {
	total: number;
	listaTipoYEstado: TipoYEstadoItem[];
};

/**
 * Función que construye todas las URLs de backend.
 * Requiere un objeto de parámetros que puede contener las propiedades necesarias para todas las rutas dinámicas.
 * Las rutas estáticas usan un objeto vacío como fallback cuando se llaman desde las funciones de servicio.
 */
const getBackendUrl = ({ idSubsanacion, nifEmisor, idTipoSector, idTramite, idTipoSubsanacion }: IParams) => ({
	// Trámites
	POST_CANCELAR_OPERACION: `${getBaseUrl()}/tramites/cancelacion`,
	POST_AMPLIACION_VENCIMIENTO: `${getBaseUrl()}/tramites/ampliacion-vencimiento`,
	GET_DETAIL: `${getBaseUrl()}/tramites/detalle/${nifEmisor}/${idTipoSector}/${idTramite}`,
	POST_OPERATIONS: `${getBaseUrl()}/tramites/listado`,
	POST_GUARDAR_BORRADOR: `${getBaseUrl()}/tramites/borrador`,
	POST_ENVIAR_BORRADOR: `${getBaseUrl()}/tramites`, // Endpoint de envío final
	POST_TRAMITES_ESTADISTICAS: `${getBaseUrl()}/tramites/estadisticas`,

	// Subsanaciones
	POST_SUBSANACIONES: `${getBaseUrl()}/subsanaciones/listado`,
	GET_SUBSANACIONES_DETAIL: `${getBaseUrl()}/subsanaciones/detalle/${nifEmisor}/${idTipoSector}/${idTramite}/${idTipoSubsanacion}/${idSubsanacion}`,
	POST_SUBSANACIONES_ESTADISTICAS: `${getBaseUrl()}/subsanaciones/estadisticas`,
	POST_SUBSANACIONES_SOLICITUDES_DOCUMENTACION_LISTADO: `${getBaseUrl()}/subsanaciones/solicitudes-documentacion/listado`,
	POST_SUBSANACIONES_ERRORES_DOCUMENTO: `${getBaseUrl()}/subsanaciones/errores/documento`,
	POST_SUBSANACIONES_ERRORES_SUJETO: `${getBaseUrl()}/subsanaciones/errores/sujeto`,
	POST_SUBSANACIONES_SIN_ERRORES: `${getBaseUrl()}/subsanaciones/sin-errores`,

	// Documentos
	POST_DOCUMENTO_CONTENIDO: `${getBaseUrl()}/documentos/contenido`,
	POST_DOCUMENTO_PODER_CONTENIDO: `${getBaseUrl()}/documentos/poder/contenido`,
	POST_DOCUMENTO_POST_FIRMA_CONTENIDO: `${getBaseUrl()}/documentos/post-firma/contenido`,

	// Firma
	POST_REVISION_FIRMA: `${getBaseUrl()}/tramites/revision-firma`,
	POST_ELIMINACION_FIRMA: `${getBaseUrl()}/tramites/revision-firma/cancelacion`,
});

// --- Funciones de Llamada a Servicios (Tipos 'any') ---

// Trámites
const getTramiteDetail = async (data: ITramiteGetPayload) =>
	callGetExternalApi<ITramiteTMU>({ axiosInstance, withCredentials: true, url: getBackendUrl(data).GET_DETAIL, processError });

const postTramitesEstadisticas = async () =>
	callPostExternalApi<EstadisticaResponse | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_TRAMITES_ESTADISTICAS,
		processError
	});

const postGuardarBorrador = async (data: ITramite) =>
	callPostExternalApi<string>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_GUARDAR_BORRADOR,
		data,
		processError
	});

const postAmpliacionVencimiento = async (data: {
	vigencia: string;
	nifEmisor: string;
	idTramite: number;
	tipoSector: ILookup;
}) =>
	callPostExternalApi<undefined | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_AMPLIACION_VENCIMIENTO,
		data,
		processError
	});

const postCancelarOperacion = async (data: CancelacionPayload) =>
	callPostExternalApi<undefined | IErrorResponse>({
		axiosInstance,
		processError,
		withCredentials: true,
		url: getBackendUrl({}).POST_CANCELAR_OPERACION,
		data,
	});

const postEnviarBorrador = async (data: { nifEmisor: string; tipoSector: ILookup; idSolicitud: number }) =>
	callPostExternalApi<undefined | IErrorResponse>({
		axiosInstance,
		processError,
		withCredentials: true,
		url: getBackendUrl({}).POST_ENVIAR_BORRADOR,
		data,
	});

const postOperations = async (data: ITramitesListadoPayload) =>
	callPostExternalApi<ITramitesListadoResponse | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_OPERATIONS,
		data,
		processError
	});

// Subsanaciones
const postSubsanaciones = async (data: ISubsanacionListadoPayload) =>
	callPostExternalApi<ISubsanacionListadoResponse | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_SUBSANACIONES,
		data,
		processError
	});

const getDetailSubsanaciones = async (payload: {
	nifEmisor: string;
	idTipoSector: number;
	idTramite: number;
	idTipoSubsanacion: number;
	idSubsanacion: number;
}) =>
	callGetExternalApi<ISolicitudDocumentacion | ISubsanacionDocumento | ISubsanacionSujeto | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl(payload).GET_SUBSANACIONES_DETAIL,
		processError
	});

const postSubsanacionesEstadisticas = async () =>
	callPostExternalApi<EstadisticaResponse | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_SUBSANACIONES_ESTADISTICAS,
		processError
	});

const postSubsanacionesSolicitudesDocumentacionListado = async (data: ISolicitudDocumentacionPayload) =>
	callPostExternalApi<ISolicitudDocumentacionResponse | IErrorResponse>({
		axiosInstance,
		data,
		withCredentials: true,
		url: getBackendUrl({}).POST_SUBSANACIONES_SOLICITUDES_DOCUMENTACION_LISTADO,
		processError
	});

const postSubsanacionesErroresDocumento = async (data: IErroresDocumentoPayload) =>
	callPostExternalApi<undefined | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_SUBSANACIONES_ERRORES_DOCUMENTO,
		data,
		processError
	});

const postSubsanacionesErroresSujeto = async (data: IErroresSujetoPayload) =>
	callPostExternalApi<any>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_SUBSANACIONES_ERRORES_SUJETO,
		data,
		processError
	});

const postSubsanacionesSinErrores = async (data: ISubsancionSinErroresPayload) =>
	callPostExternalApi<undefined | IErrorResponse>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_SUBSANACIONES_SIN_ERRORES,
		data,
		processError
	});

// Documentos
const postDocumentoContenido = async (data: DocumentoContenidoPayload) =>
	callPostExternalApi<string>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_DOCUMENTO_CONTENIDO,
		data,
		processError
	});

const postDocumentoPoderContenido = async (data: any) =>
	callPostExternalApi<any>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_DOCUMENTO_PODER_CONTENIDO,
		data,
		processError
	});

const postDocumentoPostFirmaContenido = async (data: any) =>
	callPostExternalApi<any>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_DOCUMENTO_POST_FIRMA_CONTENIDO,
		data,
		processError
	});

// Firma
const postRevisionFirma = async (data: any) =>
	callPostExternalApi<any>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_REVISION_FIRMA,
		data,
		processError
	});

const postEliminacionFirma = async (data: any) =>
	callPostExternalApi<any>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrl({}).POST_ELIMINACION_FIRMA,
		data,
		processError
	});

export {
	getDetailSubsanaciones,
	getTramiteDetail,
	postAmpliacionVencimiento,
	postCancelarOperacion,
	postDocumentoContenido,
	postDocumentoPoderContenido,
	postDocumentoPostFirmaContenido,
	postEnviarBorrador,
	postGuardarBorrador,
	postOperations,
	postSubsanaciones,
	postSubsanacionesErroresDocumento,
	postSubsanacionesErroresSujeto,
	postSubsanacionesEstadisticas,
	postSubsanacionesSinErrores,
	postSubsanacionesSolicitudesDocumentacionListado,
	postTramitesEstadisticas,
	postRevisionFirma,
	postEliminacionFirma,
};

